import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
  host,
  employeesApi,
  payrollEmployeeSalariesApi,
  payrollItemsApi,
  payrollEmployeeIncentivesBaseApi,
  payrollPayslipsApi,
  payrollRecurringIncentivesApi,
  payrollMonthlyIncentivesApi,
  payrollEmployeePayrollItemApi,
} from "proxyUrl";

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const monthISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

// Helper function to format date string (YYYY-MM-DD) to display format (DD-MM-YYYY) in local timezone
const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  // Parse YYYY-MM-DD in local timezone
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
};

// Helper function to parse date string (YYYY-MM-DD) to Date object in local timezone
const parseDateString = (dateString) => {
  if (!dateString) return null;
  // Parse YYYY-MM-DD in local timezone
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Helper function to format Date object to YYYY-MM-DD string in local timezone
const formatDateToISO = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const SimpleModal = ({ open, title, onClose, children, footer, widthClass, slideFromRight = false }) => {
  if (!open) return null;
  
  if (slideFromRight) {
    return (
      <div className="fixed inset-0 z-50 overflow-hidden">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity duration-300"
          onClick={onClose}
        />
        {/* Side Panel */}
        <div className="fixed inset-y-0 right-0 flex max-w-full pl-10">
          <div className="relative flex w-screen max-w-md flex-col overflow-y-auto bg-white shadow-xl transform transition-transform duration-300 ease-out translate-x-0">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-5">
              <h4 className="text-xl font-bold text-gray-900">{title}</h4>
              <button
                onClick={onClose}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6">{children}</div>
            {/* Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 bg-white px-6 py-4">
              {footer}
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div
        className={`w-full rounded-2xl bg-white shadow-xl ${
          widthClass || "max-w-2xl"
        }`}
      >
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h4 className="text-lg font-semibold text-gray-800">{title}</h4>
          <button
            onClick={onClose}
            className="rounded-full border border-gray-200 px-3 py-1 text-sm text-[#444]"
          >
            Close
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-6 py-4">{children}</div>
        <div className="flex items-center justify-end gap-3 border-t px-6 py-4">
          {footer}
        </div>
      </div>
    </div>
  );
};

const SectionCard = ({ title, actions, children }) => (
  <div className="rounded-xl bg-white p-4 shadow-sm">
    <div className="mb-0 flex items-center justify-between">
      <h3 className="text-lg font-semibold text-gray-800">{title}</h3>  
      <div className="flex items-center gap-2">{actions}</div>
    </div>
    {children}
  </div>
);

const numberOrZero = (val) => (val === null || val === undefined ? 0 : Number(val));

const Payroll = () => {
  const [activeTab, setActiveTab] = useState("salaries");
  const [employees, setEmployees] = useState([]);

  // ---- Employee salaries ----
  const [salaryList, setSalaryList] = useState([]);
  const [salaryLoading, setSalaryLoading] = useState(false);
  const [salaryPagination, setSalaryPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });
  const [salaryFilters, setSalaryFilters] = useState({
    search: "",
    designation: "",
    from: "",
    to: "",
  });
  const [salaryModalOpen, setSalaryModalOpen] = useState(false);
  const [salaryForm, setSalaryForm] = useState({
    employeeId: null,
    name: "",
    basicSalary: "",
    payFrequency: "monthly",
    effectiveFrom: todayISO(),
  });

  // ---- Payroll items ----
  const [itemsTab, setItemsTab] = useState("addition");
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [itemForm, setItemForm] = useState({
    id: null,
    name: "",
    type: "addition",
    category: "",
    defaultAmount: "",
    isActive: true,
  });

  // ---- Incentives ----
  const [incentiveEmployeeId, setIncentiveEmployeeId] = useState("");
  const [incentiveMonth, setIncentiveMonth] = useState(monthISO());
  const [incentivesLoading, setIncentivesLoading] = useState(false);
  const [recurringIncentives, setRecurringIncentives] = useState([]);
  const [monthlyIncentives, setMonthlyIncentives] = useState([]);
  const [recurringModalOpen, setRecurringModalOpen] = useState(false);
  const [monthlyModalOpen, setMonthlyModalOpen] = useState(false);
  const [incentiveForm, setIncentiveForm] = useState({
    payrollItemId: "",
    amount: "",
    startMonth: monthISO(),
    endMonth: "",
    notes: "",
  });
  const [monthlyIncentiveForm, setMonthlyIncentiveForm] = useState({
    payrollItemId: "",
    amount: "",
    month: monthISO(),
    notes: "",
  });

  // ---- Employee payroll items (additions / deductions) ----
  const [employeePayrollItems, setEmployeePayrollItems] = useState([]);
  const [employeePayrollLoading, setEmployeePayrollLoading] = useState(false);
  const [employeePayrollModalOpen, setEmployeePayrollModalOpen] = useState(false);
  const [employeePayrollForm, setEmployeePayrollForm] = useState({
    id: null,
    payrollItemId: "",
    type: "addition",
    amount: "",
    effectiveFrom: todayISO(),
    effectiveTo: "",
    notes: "",
    isActive: true,
    replaceExisting: true,
  });

  // ---- Payslips ----
  const [payslips, setPayslips] = useState([]);
  const [payslipsLoading, setPayslipsLoading] = useState(false);
  const [payslipFilters, setPayslipFilters] = useState({
    employeeId: "",
    from: "",
    to: "",
  });
  const [payslipPagination, setPayslipPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    employeeId: null,
    name: "",
    periodStart: todayISO(),
    periodEnd: todayISO(),
    payDate: todayISO(),
  });
  const [showPeriodStartPicker, setShowPeriodStartPicker] = useState(false);
  const [showPeriodEndPicker, setShowPeriodEndPicker] = useState(false);
  const [showPayDatePicker, setShowPayDatePicker] = useState(false);
  const [showSalaryFilterFromPicker, setShowSalaryFilterFromPicker] = useState(false);
  const [showSalaryFilterToPicker, setShowSalaryFilterToPicker] = useState(false);
  const [showPayslipFilterFromPicker, setShowPayslipFilterFromPicker] = useState(false);
  const [showPayslipFilterToPicker, setShowPayslipFilterToPicker] = useState(false);
  const [showSalaryFormEffectiveFromPicker, setShowSalaryFormEffectiveFromPicker] = useState(false);
  const [showEmployeePayrollEffectiveFromPicker, setShowEmployeePayrollEffectiveFromPicker] = useState(false);
  const [showEmployeePayrollEffectiveToPicker, setShowEmployeePayrollEffectiveToPicker] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const incentiveItems = useMemo(
    () =>
      (items || []).filter((it) => {
        const t = (it?.type || "").toString().toLowerCase();
        return t === "incentive" && it.isActive !== false;
      }),
    [items]
  );

  const additionAndDeductionItems = useMemo(
    () =>
      (items || []).filter((it) => {
        const t = (it?.type || "").toString().toLowerCase();
        return (t === "addition" || t === "deduction") && it.isActive !== false;
      }),
    [items]
  );

  const getEmployeeNameById = (id) => {
    const emp = employees.find((e) => String(e.id) === String(id));
    if (!emp) return id || "-";
    return `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || emp.name || id;
  };

  const fetchEmployees = async () => {
    try {
      const { data } = await axios.get(employeesApi, { withCredentials: true });
      setEmployees(data?.data || data?.users || data || []);
    } catch (error) {
      console.error("employees fetch error", error);
      toast.error("Unable to load employees");
    }
  };

  const fetchSalaries = async (pageOverride) => {
    setSalaryLoading(true);
    try {
      const params = {
        page: pageOverride || salaryPagination.page,
        pageSize: salaryPagination.pageSize,
      };
      Object.entries(salaryFilters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const { data } = await axios.get(payrollEmployeeSalariesApi, {
        params,
        withCredentials: true,
      });
      setSalaryList(data?.data || []);
      if (data?.pagination) {
        setSalaryPagination(data.pagination);
      }
    } catch (error) {
      console.error("salary list error", error);
      toast.error("Unable to load employee salaries");
    } finally {
      setSalaryLoading(false);
    }
  };

  const openSalaryModal = (row) => {
    if (!row) return;
    setSalaryForm({
      employeeId: row.id,
      name: row.name,
      basicSalary: row.basicSalary || "",
      payFrequency: "monthly",
      effectiveFrom: todayISO(),
    });
    setSalaryModalOpen(true);
  };

  const saveSalary = async () => {
    if (!salaryForm.employeeId || !salaryForm.basicSalary || !salaryForm.effectiveFrom) {
      toast.error("Basic salary and effective from date are required");
      return;
    }
    try {
      const payload = {
        basicSalary: Number(salaryForm.basicSalary),
        payFrequency: salaryForm.payFrequency || "monthly",
        effectiveFrom: salaryForm.effectiveFrom,
      };
      await axios.post(
        `${payrollEmployeeIncentivesBaseApi}/${salaryForm.employeeId}/salary`,
        payload,
        { withCredentials: true }
      );
      toast.success("Salary updated");
      setSalaryModalOpen(false);
      fetchSalaries();
    } catch (error) {
      console.error("salary save error", error);
      toast.error(error?.response?.data?.message || "Unable to save salary");
    }
  };

  const fetchItems = async () => {
    setItemsLoading(true);
    try {
      const params = { type: itemsTab };
      const { data } = await axios.get(payrollItemsApi, {
        params,
        withCredentials: true,
      });
      setItems(data?.data || []);
    } catch (error) {
      console.error("payroll items error", error);
      toast.error("Unable to load payroll items");
    } finally {
      setItemsLoading(false);
    }
  };

  const openItemModal = (record) => {
    if (record) {
      setItemForm({
        id: record.id,
        name: record.name || "",
        type: record.type || itemsTab,
        category: record.category || "",
        defaultAmount:
          record.defaultAmount === null || record.defaultAmount === undefined
            ? ""
            : record.defaultAmount,
        isActive: record.isActive !== false,
      });
    } else {
      setItemForm({
        id: null,
        name: "",
        type: itemsTab,
        category: "",
        defaultAmount: "",
        isActive: true,
      });
    }
    setItemModalOpen(true);
  };

  const saveItem = async () => {
    if (!itemForm.name) {
      toast.error("Name is required");
      return;
    }
    if (!itemForm.type) {
      toast.error("Type is required");
      return;
    }
    try {
      const payload = {
        name: itemForm.name,
        type: itemForm.type,
        category: itemForm.category || null,
        defaultAmount:
          itemForm.defaultAmount === "" ? 0 : Number(itemForm.defaultAmount),
        isActive: itemForm.isActive,
      };
      if (itemForm.id) {
        await axios.put(`${payrollItemsApi}/${itemForm.id}`, payload, {
          withCredentials: true,
        });
        toast.success("Payroll item updated");
      } else {
        await axios.post(payrollItemsApi, payload, { withCredentials: true });
        toast.success("Payroll item created");
      }
      setItemModalOpen(false);
      fetchItems();
    } catch (error) {
      console.error("payroll item save error", error);
      toast.error("Unable to save payroll item");
    }
  };

  const deactivateItem = async (record) => {
    const confirmed = window.confirm(
      `Deactivate "${record.name}"? It will no longer be available for future payroll.`
    );
    if (!confirmed) return;
    try {
      await axios.delete(`${payrollItemsApi}/${record.id}`, {
        withCredentials: true,
      });
      toast.success("Payroll item deactivated");
      fetchItems();
    } catch (error) {
      console.error("payroll item delete error", error);
      toast.error("Unable to deactivate item");
    }
  };

  const fetchIncentives = async () => {
    if (!incentiveEmployeeId || !incentiveMonth) return;
    setIncentivesLoading(true);
    try {
      const params = { month: `${incentiveMonth}-01` };
      const { data } = await axios.get(
        `${payrollEmployeeIncentivesBaseApi}/${incentiveEmployeeId}/incentives`,
        {
          params,
          withCredentials: true,
        }
      );
      setRecurringIncentives(data?.recurring || []);
      setMonthlyIncentives(data?.monthly || []);
    } catch (error) {
      console.error("incentives list error", error);
      toast.error("Unable to load incentives");
    } finally {
      setIncentivesLoading(false);
    }
  };

  const fetchEmployeePayrollItems = async (employeeIdOverride) => {
    const empId = employeeIdOverride || incentiveEmployeeId;
    if (!empId) return;
    setEmployeePayrollLoading(true);
    try {
      const { data } = await axios.get(
        `${payrollEmployeeIncentivesBaseApi}/${empId}/payroll-items`,
        { withCredentials: true }
      );
      setEmployeePayrollItems(data?.data || []);
    } catch (error) {
      console.error("employee payroll items list error", error);
      toast.error("Unable to load employee payroll items");
    } finally {
      setEmployeePayrollLoading(false);
    }
  };

  const saveRecurringIncentive = async () => {
    if (!incentiveEmployeeId) {
      toast.error("Select an employee");
      return;
    }
    if (!incentiveForm.payrollItemId || !incentiveForm.amount || !incentiveForm.startMonth) {
      toast.error("Item, amount and start month are required");
      return;
    }
    try {
      const payload = {
        payrollItemId: incentiveForm.payrollItemId,
        amount: Number(incentiveForm.amount),
        startMonth: `${incentiveForm.startMonth}-01`,
        endMonth: incentiveForm.endMonth
          ? `${incentiveForm.endMonth}-01`
          : null,
        notes: incentiveForm.notes || null,
        isRecurring: true,
      };
      await axios.post(
        `${payrollEmployeeIncentivesBaseApi}/${incentiveEmployeeId}/incentives/recurring`,
        payload,
        { withCredentials: true }
      );
      toast.success("Recurring incentive added");
      setRecurringModalOpen(false);
      fetchIncentives();
    } catch (error) {
      console.error("recurring incentive save error", error);
      toast.error("Unable to save recurring incentive");
    }
  };

  const deleteRecurring = async (id) => {
    if (!id) return;
    const confirmed = window.confirm("Delete this recurring incentive?");
    if (!confirmed) return;
    try {
      await axios.delete(`${payrollRecurringIncentivesApi}/${id}`, {
        withCredentials: true,
      });
      toast.success("Recurring incentive deleted");
      // remove from local state to avoid extra round-trip
      setRecurringIncentives((prev) => prev.filter((r) => r.id !== id));
    } catch (error) {
      console.error("delete recurring incentive error", error);
      toast.error("Unable to delete recurring incentive");
    }
  };

  const saveMonthlyIncentive = async () => {
    if (!incentiveEmployeeId) {
      toast.error("Select an employee");
      return;
    }
    if (!monthlyIncentiveForm.payrollItemId) {
      toast.error("Please select an incentive item");
      return;
    }
    if (
      monthlyIncentiveForm.amount === "" ||
      monthlyIncentiveForm.amount === null ||
      monthlyIncentiveForm.amount === undefined
    ) {
      toast.error("Please enter the incentive amount");
      return;
    }
    if (!monthlyIncentiveForm.month) {
      toast.error("Please select the month");
      return;
    }
    try {
      const payload = {
        payrollItemId: monthlyIncentiveForm.payrollItemId,
        amount: Number(monthlyIncentiveForm.amount),
        month: `${monthlyIncentiveForm.month}-01`,
        notes: monthlyIncentiveForm.notes || null,
      };
      await axios.post(
        `${payrollEmployeeIncentivesBaseApi}/${incentiveEmployeeId}/incentives/monthly`,
        payload,
        { withCredentials: true }
      );
      toast.success("Monthly incentive added");
      setMonthlyModalOpen(false);
      fetchIncentives();
    } catch (error) {
      console.error("monthly incentive save error", error);
      toast.error("Unable to save monthly incentive");
    }
  };

  const deleteMonthly = async (id) => {
    if (!id) return;
    const confirmed = window.confirm("Delete this monthly incentive?");
    if (!confirmed) return;
    try {
      await axios.delete(`${payrollMonthlyIncentivesApi}/${id}`, {
        withCredentials: true,
      });
      toast.success("Monthly incentive deleted");
      setMonthlyIncentives((prev) => prev.filter((r) => r.id !== id));
    } catch (error) {
      console.error("delete monthly incentive error", error);
      toast.error("Unable to delete monthly incentive");
    }
  };

  const fetchPayslips = async (pageOverride) => {
    setPayslipsLoading(true);
    try {
      const params = {
        page: pageOverride || payslipPagination.page,
        pageSize: payslipPagination.pageSize,
      };
      Object.entries(payslipFilters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const { data } = await axios.get(payrollPayslipsApi, {
        params,
        withCredentials: true,
      });
      setPayslips(data?.data || []);
      if (data?.pagination) setPayslipPagination(data.pagination);
    } catch (error) {
      console.error("payslips list error", error);
      toast.error("Unable to load payslips");
    } finally {
      setPayslipsLoading(false);
    }
  };

  const openGenerateModalFromEmployee = (row) => {
    setGenerateForm({
      employeeId: row.id,
      name: row.name,
      periodStart: todayISO(),
      periodEnd: todayISO(),
      payDate: todayISO(),
    });
    setPreviewData(null);
    setGenerateModalOpen(true);
  };

  const loadPreview = async () => {
    if (!generateForm.employeeId || !generateForm.periodStart || !generateForm.periodEnd) {
      toast.error("Period start & end are required");
      return;
    }
    setPreviewLoading(true);
    try {
      const params = {
        periodStart: generateForm.periodStart,
        periodEnd: generateForm.periodEnd,
        payDate: generateForm.payDate,
      };
      const { data } = await axios.get(
        `${payrollEmployeeIncentivesBaseApi}/${generateForm.employeeId}/payslips/preview`,
        {
          params,
          withCredentials: true,
        }
      );
      setPreviewData(data?.data || null);
    } catch (error) {
      console.error("payslip preview error", error);
      toast.error(error?.response?.data?.message || "Unable to preview payslip");
    } finally {
      setPreviewLoading(false);
    }
  };

  const generatePayslip = async () => {
    if (!generateForm.employeeId || !generateForm.periodStart || !generateForm.periodEnd) {
      toast.error("Period start & end are required");
      return;
    }
    try {
      const payload = {
        periodStart: generateForm.periodStart,
        periodEnd: generateForm.periodEnd,
        payDate: generateForm.payDate,
      };
      await axios.post(
        `${payrollEmployeeIncentivesBaseApi}/${generateForm.employeeId}/payslips/generate`,
        payload,
        { withCredentials: true }
      );
      toast.success("Payslip generated");
      setGenerateModalOpen(false);
      fetchPayslips();
    } catch (error) {
      console.error("payslip generate error", error);
      toast.error(error?.response?.data?.message || "Unable to generate payslip");
    }
  };

  const downloadPayslip = (id) => {
    if (!id) return;
    const url = `${host}/api/payroll/payslips/${id}/download`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Close date pickers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (!target.closest('.react-calendar') && !target.closest('input[readonly]')) {
        if (showPeriodStartPicker) setShowPeriodStartPicker(false);
        if (showPeriodEndPicker) setShowPeriodEndPicker(false);
        if (showPayDatePicker) setShowPayDatePicker(false);
        if (showSalaryFilterFromPicker) setShowSalaryFilterFromPicker(false);
        if (showSalaryFilterToPicker) setShowSalaryFilterToPicker(false);
        if (showPayslipFilterFromPicker) setShowPayslipFilterFromPicker(false);
        if (showPayslipFilterToPicker) setShowPayslipFilterToPicker(false);
        if (showSalaryFormEffectiveFromPicker) setShowSalaryFormEffectiveFromPicker(false);
        if (showEmployeePayrollEffectiveFromPicker) setShowEmployeePayrollEffectiveFromPicker(false);
        if (showEmployeePayrollEffectiveToPicker) setShowEmployeePayrollEffectiveToPicker(false);
      }
    };

    const anyPickerOpen = showPeriodStartPicker || showPeriodEndPicker || showPayDatePicker ||
                         showSalaryFilterFromPicker || showSalaryFilterToPicker ||
                         showPayslipFilterFromPicker || showPayslipFilterToPicker ||
                         showSalaryFormEffectiveFromPicker || showEmployeePayrollEffectiveFromPicker ||
                         showEmployeePayrollEffectiveToPicker;
    
    if (anyPickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showPeriodStartPicker, showPeriodEndPicker, showPayDatePicker, showSalaryFilterFromPicker,
      showSalaryFilterToPicker, showPayslipFilterFromPicker, showPayslipFilterToPicker,
      showSalaryFormEffectiveFromPicker, showEmployeePayrollEffectiveFromPicker, showEmployeePayrollEffectiveToPicker]);

  useEffect(() => {
    fetchEmployees();
    fetchSalaries();
    setItemsTab("addition");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === "items") {
      fetchItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsTab, activeTab]);

  // When switching to incentives tab, automatically focus "incentive" items
  useEffect(() => {
    if (activeTab === "incentives") {
      setItemsTab("incentive");
      fetchItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "payslips") {
      fetchPayslips();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const salaryTotal = useMemo(
    () =>
      salaryList.reduce(
        (acc, row) => acc + numberOrZero(row.basicSalary),
        0
      ),
    [salaryList]
  );

  const incentivesTotals = useMemo(() => {
    const recurringTotal = recurringIncentives.reduce(
      (acc, r) => acc + numberOrZero(r.amount),
      0
    );
    const monthlyTotal = monthlyIncentives.reduce(
      (acc, r) => acc + numberOrZero(r.amount),
      0
    );
    return { recurringTotal, monthlyTotal, grandTotal: recurringTotal + monthlyTotal };
  }, [recurringIncentives, monthlyIncentives]);

  return (
    <div className="space-y-6">
      <div className="relative mx-4 mt-6 overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-blue-500 to-indigo-600 p-4 text-white shadow-xl">
        <div className="relative z-10">
          <h1 className="text-xl font-bold">Salary & Payroll Management</h1>
          <p className="mt-2 text-sm opacity-90">
            Configure salaries, payroll items, incentives and payslips.
          </p>
        </div>
      </div>

      <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-100">
        {/* Tabs Section */}
        <div className="mb-3 border-b border-gray-200">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {["salaries", "items", "incentives", "payslips"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-5 py-2.5 text-sm font-semibold capitalize rounded-t-lg transition-all duration-200 ${
                    activeTab === tab
                      ? "bg-indigo-600 text-white shadow-sm"
                      : "text-[#444] hover:text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
            {activeTab === "items" && (
              <div className="flex items-center gap-2 mb-1">
                <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
                  {[
                    { key: "addition", label: "Additions" },
                    { key: "deduction", label: "Deductions" },
                    { key: "incentive", label: "Incentives" },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setItemsTab(tab.key)}
                      className={`rounded-md px-4 py-1.5 text-xs font-semibold transition-all duration-200 ${
                        itemsTab === tab.key
                          ? "bg-indigo-600 text-white shadow-sm"
                          : "text-[#444] hover:text-gray-800 hover:bg-white"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => openItemModal(null)}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-indigo-700 hover:shadow-md"
                >
                  New Item
                </button>
              </div>
            )}
            {activeTab === "incentives" && (
              <div className="flex items-center gap-2 mb-1">
                <button
                  onClick={() => {
                    // refresh available incentive items when opening modal
                    fetchItems();
                    setIncentiveForm({
                      payrollItemId: "",
                      amount: "",
                      startMonth: incentiveMonth,
                      endMonth: "",
                      notes: "",
                    });
                    setRecurringModalOpen(true);
                  }}
                  className="rounded-lg border border-indigo-300 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition-all hover:bg-indigo-50 hover:border-indigo-400"
                >
                  Add Recurring
                </button>
                <button
                  onClick={() => {
                    // refresh available incentive items when opening modal
                    fetchItems();
                    setMonthlyIncentiveForm({
                      payrollItemId: "",
                      amount: "",
                      month: incentiveMonth,
                      notes: "",
                    });
                    setMonthlyModalOpen(true);
                  }}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-indigo-700 hover:shadow-md"
                >
                  Add Monthly
                </button>
              </div>
            )}
          </div>
        </div>

        {activeTab === "salaries" && (
          <div className="space-y-6">
            {/* Search/Filter Section */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[200px]">
                  <label className="mb-2 block text-xs font-normal text-[#444] uppercase tracking-wide">
                    Name / email / code
                  </label>
                  <input
                    type="text"
                    value={salaryFilters.search}
                    onChange={(e) =>
                      setSalaryFilters((prev) => ({
                        ...prev,
                        search: e.target.value,
                      }))
                    }
                    placeholder="Name / email / code"
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                  />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="mb-2 block text-xs font-normal text-[#444] uppercase tracking-wide">
                    DESIGNATION
                  </label>
                  <input
                    type="text"
                    value={salaryFilters.designation}
                    onChange={(e) =>
                      setSalaryFilters((prev) => ({
                        ...prev,
                        designation: e.target.value,
                      }))
                    }
                    placeholder="Designation"
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                  />
                </div>
                <div className="flex-1 min-w-[180px]">
                  <label className="mb-2 block text-xs font-normal text-[#444] uppercase tracking-wide">
                    FROM
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      readOnly
                      value={formatDateForDisplay(salaryFilters.from)}
                      onClick={() => {
                        setShowSalaryFilterFromPicker(!showSalaryFilterFromPicker);
                        setShowSalaryFilterToPicker(false);
                      }}
                      className="w-full rounded-lg border border-gray-200 bg-white pl-3 pr-11 py-2.5 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all cursor-pointer"
                      placeholder=""
                    />
                    {!salaryFilters.from && (
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                        <span className="text-sm text-gray-400">Select date</span>
                      </div>
                    )}
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    {showSalaryFilterFromPicker && (
                      <div className="absolute top-full left-0 z-50 mt-2 p-2.5">
                        <Calendar
                          onChange={(date) => {
                            const formattedDate = date ? formatDateToISO(date) : '';
                            setSalaryFilters((prev) => ({ ...prev, from: formattedDate }));
                            setShowSalaryFilterFromPicker(false);
                          }}
                          value={parseDateString(salaryFilters.from)}
                          formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                          prevLabel={<span className="text-purple-600">‹</span>}
                          nextLabel={<span className="text-purple-600">›</span>}
                          prev2Label={null}
                          next2Label={null}
                          tileClassName={({ date, view }) => {
                            if (view === 'month') {
                              const day = date.getDay();
                              if (day === 0 || day === 6) {
                                return 'weekend-day';
                              }
                            }
                            return null;
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-1 min-w-[180px]">
                  <label className="mb-2 block text-xs font-normal text-[#444] uppercase tracking-wide">
                    TO
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      readOnly
                      value={formatDateForDisplay(salaryFilters.to)}
                      onClick={() => {
                        setShowSalaryFilterToPicker(!showSalaryFilterToPicker);
                        setShowSalaryFilterFromPicker(false);
                      }}
                      className="w-full rounded-lg border border-gray-200 bg-white pl-3 pr-11 py-2.5 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all cursor-pointer"
                      placeholder=""
                    />
                    {!salaryFilters.to && (
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                        <span className="text-sm text-gray-400">Select date</span>
                      </div>
                    )}
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    {showSalaryFilterToPicker && (
                      <div className="absolute top-full right-0 z-50 mt-2 p-2.5">
                        <Calendar
                          onChange={(date) => {
                            const formattedDate = date ? formatDateToISO(date) : '';
                            setSalaryFilters((prev) => ({ ...prev, to: formattedDate }));
                            setShowSalaryFilterToPicker(false);
                          }}
                          value={parseDateString(salaryFilters.to)}
                          formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                          prevLabel={<span className="text-purple-600">‹</span>}
                          nextLabel={<span className="text-purple-600">›</span>}
                          prev2Label={null}
                          next2Label={null}
                          tileClassName={({ date, view }) => {
                            if (view === 'month') {
                              const day = date.getDay();
                              if (day === 0 || day === 6) {
                                return 'weekend-day';
                              }
                            }
                            return null;
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => fetchSalaries(1)}
                    className="rounded-lg bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-indigo-700 hover:shadow-md"
                  >
                    Apply
                  </button>
                  <button
                    onClick={() => {
                      const cleared = {
                        search: "",
                        designation: "",
                        from: "",
                        to: "",
                      };
                      setSalaryFilters(cleared);
                      setSalaryPagination((prev) => ({ ...prev, page: 1 }));
                      fetchSalaries(1);
                    }}
                    className="rounded-lg border border-gray-300 bg-white px-6 py-2.5 text-sm font-semibold text-[#444] shadow-sm transition-all hover:bg-gray-50 hover:border-gray-400"
                  >
                    Reset
                  </button>
                </div>
              </div>
              <div className="mt-5 flex justify-end border-t border-gray-100 pt-4">
                <div className="text-sm font-medium text-[#444]">
                  Total basic salary this page:{" "}
                  <span className="font-bold text-gray-900">
                    {salaryTotal.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-[#444]">
                        EMPLOYEE
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-[#444]">
                        EMAIL / PHONE
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-[#444]">
                        DESIGNATION
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-[#444]">
                        DOJ
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-[#444]">
                        BASIC SALARY
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-[#444]">
                        ACTIONS
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {salaryLoading && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-6 py-8 text-center text-sm text-gray-500"
                        >
                          Loading...
                        </td>
                      </tr>
                    )}
                    {!salaryLoading && salaryList.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-6 py-8 text-center text-sm text-gray-500"
                        >
                          No employees found
                        </td>
                      </tr>
                    )}
                    {salaryList.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="flex flex-col">
                            <span className="font-semibold">{row.name}</span>
                            <span className="text-xs text-gray-500">
                              Code: {row.employeeCode || "-"}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#444]">
                          <div className="flex flex-col">
                            <span>{row.email}</span>
                            <span className="text-xs text-gray-500">
                              {row.phone || "-"}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#444]">
                          {row.designation || "-"}
                        </td>
                        <td className="px-6 py-4 text-sm text-[#444]">
                          {row.doj || "-"}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                          {numberOrZero(row.basicSalary).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-right text-sm">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => openSalaryModal(row)}
                              className="rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 transition-all hover:bg-indigo-50 hover:border-indigo-400"
                            >
                              Update Salary
                            </button>
                            <button
                              onClick={() => {
                                setIncentiveEmployeeId(row.id);
                                // Load additions/deductions items for the dropdown
                                setItemsTab("addition");
                                fetchItems();
                                // Load this employee's existing payroll items (pass ID directly to avoid async state issue)
                                fetchEmployeePayrollItems(row.id);
                                // Reset form for new item
                                setEmployeePayrollForm({
                                  id: null,
                                  payrollItemId: "",
                                  type: "addition",
                                  amount: "",
                                  effectiveFrom: todayISO(),
                                  effectiveTo: "",
                                  notes: "",
                                  isActive: true,
                                  replaceExisting: true,
                                });
                                setEmployeePayrollModalOpen(true);
                              }}
                              className="rounded-lg border border-green-300 bg-white px-3 py-1.5 text-xs font-semibold text-green-700 transition-all hover:bg-green-50 hover:border-green-400"
                            >
                              Add Payroll Item
                            </button>
                            <button
                              onClick={() => openGenerateModalFromEmployee(row)}
                              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-all hover:bg-indigo-700 hover:shadow-md"
                            >
                              Generate Payslip
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "items" && (
          <div className="space-y-4">
            <SectionCard
              title=""
              actions={null}
            >
              <div className="overflow-hidden rounded-xl border border-gray-100">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-[#444]">
                          NAME
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-[#444]">
                          CATEGORY
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-[#444]">
                          DEFAULT AMOUNT
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-[#444]">
                          STATUS
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-[#444]">
                          ACTIONS
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {itemsLoading && (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-6 py-8 text-center text-sm text-gray-500"
                          >
                            Loading...
                          </td>
                        </tr>
                      )}
                      {!itemsLoading && items.length === 0 && (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-6 py-8 text-center text-sm text-gray-500"
                          >
                            No items found
                          </td>
                        </tr>
                      )}
                      {items.map((row) => (
                        <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                            {row.name}
                          </td>
                          <td className="px-6 py-4 text-sm text-indigo-600">
                            {row.category || "-"}
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-gray-900">
                            {numberOrZero(row.defaultAmount).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 text-center text-sm">
                            <span
                              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                                row.isActive !== false
                                  ? "bg-green-100 text-green-700"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {row.isActive !== false ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right text-sm">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => openItemModal(row)}
                                className="rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 transition-all hover:bg-indigo-50 hover:border-indigo-400"
                              >
                                Edit
                              </button>
                              {row.isActive !== false && (
                                <button
                                  onClick={() => deactivateItem(row)}
                                  className="rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 transition-all hover:bg-red-50 hover:border-red-400"
                                >
                                  Deactivate
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </SectionCard>
          </div>
        )}

        {activeTab === "incentives" && (
          <div className="space-y-4">
            <SectionCard
              title=""
              actions={null}
            >
              <div className="mb-4 flex justify-end">
                <div className="w-full md:w-1/2 flex flex-wrap items-end gap-4">
                  <div className="flex-1 min-w-[150px]">
                    <label className="mb-2 block text-xs font-normal text-[#444] uppercase tracking-wide">
                      Employee
                    </label>
                    <div className="relative">
                      <select
                        value={incentiveEmployeeId}
                        onChange={(e) => setIncentiveEmployeeId(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-10 text-sm text-[#444] focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all appearance-none"
                        style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
                      >
                        <option value="">Select employee</option>
                        {employees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {getEmployeeNameById(emp.id)}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 min-w-[150px]">
                    <label className="mb-2 block text-xs font-normal text-[#444] uppercase tracking-wide">
                      Month
                    </label>
                    <input
                      type="month"
                      value={incentiveMonth}
                      onChange={(e) => setIncentiveMonth(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-[#444] placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all"
                    />
                  </div>
                  <div className="shrink-0">
                    <button
                      onClick={() => {
                        fetchIncentives();
                        fetchEmployeePayrollItems();
                      }}
                      className="rounded-lg bg-gradient-to-r from-indigo-600 to-indigo-500 px-6 py-2.5 text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all hover:from-indigo-700 hover:to-indigo-600"
                    >
                      Load Incentives
                    </button>
                  </div>
                </div>
              </div>

              {(!incentiveEmployeeId || !incentiveMonth) && (
                <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 p-12">
                  <svg className="h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-sm font-medium text-gray-600 mb-1">No incentives selected</p>
                  <p className="text-xs text-gray-500">Select employee and month to view incentives</p>
                </div>
              )}

              {incentiveEmployeeId && incentiveMonth && (
                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                    <div className="bg-gradient-to-r from-indigo-600 to-indigo-500 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-white">
                          Recurring Incentives
                        </h4>
                        <span className="text-xs text-indigo-100">
                          Total:{" "}
                          <span className="font-semibold text-white">
                            {incentivesTotals.recurringTotal.toLocaleString()}
                          </span>
                        </span>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-100">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#444]">
                              Item
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#444]">
                              Duration
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-[#444]">
                              Amount
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-[#444]">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {incentivesLoading && (
                            <tr>
                              <td
                                colSpan={4}
                                className="px-6 py-8 text-center text-sm text-gray-500"
                              >
                                <div className="flex items-center justify-center">
                                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent"></div>
                                  <span className="ml-2">Loading...</span>
                                </div>
                              </td>
                            </tr>
                          )}
                          {!incentivesLoading &&
                            recurringIncentives.length === 0 && (
                              <tr>
                                <td
                                  colSpan={4}
                                  className="px-6 py-8 text-center text-sm text-gray-500"
                                >
                                  <div className="flex flex-col items-center">
                                    <svg className="h-8 w-8 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                    </svg>
                                    <p className="text-gray-400">No recurring incentives</p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          {recurringIncentives.map((row) => (
                            <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                {row.payrollItem?.name || "-"}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-600">
                                {row.startMonth}{" "}
                                {row.endMonth ? `- ${row.endMonth}` : "• Ongoing"}
                              </td>
                              <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                                {numberOrZero(row.amount).toLocaleString()}
                              </td>
                              <td className="px-6 py-4 text-right text-sm">
                                <button
                                  onClick={() => deleteRecurring(row.id)}
                                  className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 hover:border-red-300 transition-colors"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                    <div className="bg-gradient-to-r from-indigo-600 to-indigo-500 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-white">
                          Monthly Incentives ({incentiveMonth})
                        </h4>
                        <span className="text-xs text-indigo-100">
                          Total:{" "}
                          <span className="font-semibold text-white">
                            {incentivesTotals.monthlyTotal.toLocaleString()}
                          </span>
                        </span>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-100">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#444]">
                              Item
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#444]">
                              Notes
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-[#444]">
                              Amount
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-[#444]">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {incentivesLoading && (
                            <tr>
                              <td
                                colSpan={4}
                                className="px-6 py-8 text-center text-sm text-gray-500"
                              >
                                <div className="flex items-center justify-center">
                                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent"></div>
                                  <span className="ml-2">Loading...</span>
                                </div>
                              </td>
                            </tr>
                          )}
                          {!incentivesLoading &&
                            monthlyIncentives.length === 0 && (
                              <tr>
                                <td
                                  colSpan={4}
                                  className="px-6 py-8 text-center text-sm text-gray-500"
                                >
                                  <div className="flex flex-col items-center">
                                    <svg className="h-8 w-8 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                    </svg>
                                    <p className="text-gray-400">No monthly incentives</p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          {monthlyIncentives.map((row) => (
                            <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                {row.payrollItem?.name || "-"}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-600">
                                {row.notes || "-"}
                              </td>
                              <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                                {numberOrZero(row.amount).toLocaleString()}
                              </td>
                              <td className="px-6 py-4 text-right text-sm">
                                <button
                                  onClick={() => deleteMonthly(row.id)}
                                  className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 hover:border-red-300 transition-colors"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {incentiveEmployeeId && incentiveMonth && (
                <div className="space-y-4 mt-4">
                  <div className="rounded-lg bg-gray-50 px-4 py-3 text-xs text-gray-600">
                    <span className="font-semibold text-gray-800">
                      Total incentives for {getEmployeeNameById(incentiveEmployeeId)}:
                    </span>{" "}
                    {incentivesTotals.grandTotal.toLocaleString()}
                  </div>
                  <SectionCard
                    title="Additions & Deductions (applied to salary)"
                    actions={
                      <button
                        onClick={() => {
                          if (!incentiveEmployeeId) {
                            toast.error("Select an employee first");
                            return;
                          }
                          // Load this employee's existing payroll items
                          fetchEmployeePayrollItems(incentiveEmployeeId);
                          setEmployeePayrollForm({
                            id: null,
                            payrollItemId: "",
                            type: "addition",
                            amount: "",
                            effectiveFrom: todayISO(),
                            effectiveTo: "",
                            notes: "",
                            isActive: true,
                            replaceExisting: true,
                          });
                          // ensure we have the latest list of master items
                          setItemsTab("addition");
                          fetchItems();
                          setEmployeePayrollModalOpen(true);
                        }}
                        className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow hover:shadow-md"
                      >
                        Add Payroll Item
                      </button>
                    }
                  >
                    <div className="grid gap-4 lg:grid-cols-2">
                      <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
                        <div className="flex items-center justify-between border-b px-4 py-3">
                          <h4 className="text-sm font-semibold text-gray-800">
                            Additions
                          </h4>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-100">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                                  Item
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                                  Effective
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-gray-500">
                                  Amount
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-gray-500">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                              {employeePayrollLoading && (
                                <tr>
                                  <td
                                    colSpan={4}
                                    className="px-4 py-4 text-center text-sm text-gray-500"
                                  >
                                    Loading...
                                  </td>
                                </tr>
                              )}
                              {!employeePayrollLoading &&
                                employeePayrollItems.filter(
                                  (r) =>
                                    (r.payrollItem?.type || "").toLowerCase() === "addition"
                                ).length === 0 && (
                                  <tr>
                                    <td
                                      colSpan={4}
                                      className="px-4 py-4 text-center text-sm text-gray-500"
                                    >
                                      No additions applied
                                    </td>
                                  </tr>
                                )}
                              {employeePayrollItems
                                .filter(
                                  (r) =>
                                    (r.payrollItem?.type || "").toLowerCase() === "addition"
                                )
                                .map((row) => (
                                  <tr key={row.id}>
                                    <td className="px-4 py-3 text-sm text-gray-800">
                                      {row.payrollItem?.name || "-"}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-[#444]">
                                      {row.effectiveFrom}
                                      {row.effectiveTo ? ` - ${row.effectiveTo}` : ""}
                                    </td>
                                    <td className="px-4 py-3 text-right text-sm font-semibold text-gray-800">
                                      {numberOrZero(row.amount || row.payrollItem?.defaultAmount).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 text-right text-sm">
                                      <button
                                        onClick={() => {
                                setEmployeePayrollForm({
                                  id: row.id,
                                  payrollItemId: row.payrollItemId,
                                  type: (row.payrollItem?.type || "addition").toLowerCase(),
                                  amount: row.amount ?? row.payrollItem?.defaultAmount ?? "",
                                  effectiveFrom: row.effectiveFrom || todayISO(),
                                  effectiveTo: row.effectiveTo || "",
                                  notes: row.notes || "",
                                  isActive: row.isActive !== false,
                                  replaceExisting: false,
                                });
                                          setItemsTab("addition");
                                          fetchItems();
                                          setEmployeePayrollModalOpen(true);
                                        }}
                                        className="mr-2 rounded-lg border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={async () => {
                                          if (
                                            !window.confirm(
                                              "Deactivate this payroll item for the employee?"
                                            )
                                          )
                                            return;
                                          try {
                                            await axios.delete(
                                              `${payrollEmployeePayrollItemApi}/${row.id}`,
                                              { withCredentials: true }
                                            );
                                            toast.success("Payroll item deactivated");
                                            setEmployeePayrollItems((prev) =>
                                              prev.filter((x) => x.id !== row.id)
                                            );
                                          } catch (err) {
                                            console.error(
                                              "delete employee payroll item error",
                                              err
                                            );
                                            toast.error("Unable to delete payroll item");
                                          }
                                        }}
                                        className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
                        <div className="flex items-center justify-between border-b px-4 py-3">
                          <h4 className="text-sm font-semibold text-gray-800">
                            Deductions
                          </h4>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-100">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                                  Item
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                                  Effective
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-gray-500">
                                  Amount
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-gray-500">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                              {employeePayrollLoading && (
                                <tr>
                                  <td
                                    colSpan={4}
                                    className="px-4 py-4 text-center text-sm text-gray-500"
                                  >
                                    Loading...
                                  </td>
                                </tr>
                              )}
                              {!employeePayrollLoading &&
                                employeePayrollItems.filter(
                                  (r) =>
                                    (r.payrollItem?.type || "").toLowerCase() === "deduction"
                                ).length === 0 && (
                                  <tr>
                                    <td
                                      colSpan={4}
                                      className="px-4 py-4 text-center text-sm text-gray-500"
                                    >
                                      No deductions applied
                                    </td>
                                  </tr>
                                )}
                              {employeePayrollItems
                                .filter(
                                  (r) =>
                                    (r.payrollItem?.type || "").toLowerCase() === "deduction"
                                )
                                .map((row) => (
                                  <tr key={row.id}>
                                    <td className="px-4 py-3 text-sm text-gray-800">
                                      {row.payrollItem?.name || "-"}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-[#444]">
                                      {row.effectiveFrom}
                                      {row.effectiveTo ? ` - ${row.effectiveTo}` : ""}
                                    </td>
                                    <td className="px-4 py-3 text-right text-sm font-semibold text-gray-800">
                                      {numberOrZero(row.amount || row.payrollItem?.defaultAmount).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 text-right text-sm">
                                      <button
                                        onClick={() => {
                                          setEmployeePayrollForm({
                                            id: row.id,
                                            payrollItemId: row.payrollItemId,
                                            type: (row.payrollItem?.type || "deduction").toLowerCase(),
                                            amount: row.amount ?? row.payrollItem?.defaultAmount ?? "",
                                            effectiveFrom: row.effectiveFrom || todayISO(),
                                            effectiveTo: row.effectiveTo || "",
                                            notes: row.notes || "",
                                            replaceExisting: false,
                                          });
                                          setItemsTab("deduction");
                                          fetchItems();
                                          setEmployeePayrollModalOpen(true);
                                        }}
                                        className="mr-2 rounded-lg border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={async () => {
                                          if (
                                            !window.confirm(
                                              "Deactivate this payroll item for the employee?"
                                            )
                                          )
                                            return;
                                          try {
                                            await axios.delete(
                                              `${payrollEmployeePayrollItemApi}/${row.id}`,
                                              { withCredentials: true }
                                            );
                                            toast.success("Payroll item deactivated");
                                            setEmployeePayrollItems((prev) =>
                                              prev.filter((x) => x.id !== row.id)
                                            );
                                          } catch (err) {
                                            console.error(
                                              "delete employee payroll item error",
                                              err
                                            );
                                            toast.error("Unable to delete payroll item");
                                          }
                                        }}
                                        className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </SectionCard>
                </div>
              )}
            </SectionCard>
          </div>
        )}

        {activeTab === "payslips" && (
          <div className="space-y-6">
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[200px]">
                  <label className="mb-2 block text-xs font-normal text-[#444] uppercase tracking-wide">
                    Employee
                  </label>
                  <select
                    value={payslipFilters.employeeId}
                    onChange={(e) =>
                      setPayslipFilters((prev) => ({
                        ...prev,
                        employeeId: e.target.value,
                      }))
                    }
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                  >
                    <option value="">All employees</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {getEmployeeNameById(emp.id)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex-1 min-w-[180px]">
                  <label className="mb-2 block text-xs font-normal text-[#444] uppercase tracking-wide">
                    FROM
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      readOnly
                      value={formatDateForDisplay(payslipFilters.from)}
                      onClick={() => {
                        setShowPayslipFilterFromPicker(!showPayslipFilterFromPicker);
                        setShowPayslipFilterToPicker(false);
                      }}
                      className="w-full rounded-lg border border-gray-200 bg-white pl-3 pr-11 py-2.5 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all cursor-pointer"
                      placeholder=""
                    />
                    {!payslipFilters.from && (
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                        <span className="text-sm text-gray-400">Select date</span>
                      </div>
                    )}
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    {showPayslipFilterFromPicker && (
                      <div className="absolute top-full left-0 z-50 mt-2 p-2.5">
                        <Calendar
                          onChange={(date) => {
                            const formattedDate = date ? formatDateToISO(date) : '';
                            setPayslipFilters((prev) => ({ ...prev, from: formattedDate }));
                            setShowPayslipFilterFromPicker(false);
                          }}
                          value={parseDateString(payslipFilters.from)}
                          formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                          prevLabel={<span className="text-purple-600">‹</span>}
                          nextLabel={<span className="text-purple-600">›</span>}
                          prev2Label={null}
                          next2Label={null}
                          tileClassName={({ date, view }) => {
                            if (view === 'month') {
                              const day = date.getDay();
                              if (day === 0 || day === 6) {
                                return 'weekend-day';
                              }
                            }
                            return null;
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-1 min-w-[180px]">
                  <label className="mb-2 block text-xs font-normal text-[#444] uppercase tracking-wide">
                    TO
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      readOnly
                      value={formatDateForDisplay(payslipFilters.to)}
                      onClick={() => {
                        setShowPayslipFilterToPicker(!showPayslipFilterToPicker);
                        setShowPayslipFilterFromPicker(false);
                      }}
                      className="w-full rounded-lg border border-gray-200 bg-white pl-3 pr-11 py-2.5 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all cursor-pointer"
                      placeholder=""
                    />
                    {!payslipFilters.to && (
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                        <span className="text-sm text-gray-400">Select date</span>
                      </div>
                    )}
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    {showPayslipFilterToPicker && (
                      <div className="absolute top-full right-0 z-50 mt-2 p-2.5">
                        <Calendar
                          onChange={(date) => {
                            const formattedDate = date ? formatDateToISO(date) : '';
                            setPayslipFilters((prev) => ({ ...prev, to: formattedDate }));
                            setShowPayslipFilterToPicker(false);
                          }}
                          value={parseDateString(payslipFilters.to)}
                          formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                          prevLabel={<span className="text-purple-600">‹</span>}
                          nextLabel={<span className="text-purple-600">›</span>}
                          prev2Label={null}
                          next2Label={null}
                          tileClassName={({ date, view }) => {
                            if (view === 'month') {
                              const day = date.getDay();
                              if (day === 0 || day === 6) {
                                return 'weekend-day';
                              }
                            }
                            return null;
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => fetchPayslips(1)}
                    className="rounded-lg bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-indigo-700 hover:shadow-md"
                  >
                    Apply
                  </button>
                  <button
                    onClick={() => {
                      const cleared = { employeeId: "", from: "", to: "" };
                      setPayslipFilters(cleared);
                      setPayslipPagination((prev) => ({ ...prev, page: 1 }));
                      fetchPayslips(1);
                    }}
                    className="rounded-lg border border-gray-300 bg-white px-6 py-2.5 text-sm font-semibold text-[#444] shadow-sm transition-all hover:bg-gray-50 hover:border-gray-400"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-100">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                        Employee
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                        Period
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                        Pay Date
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-gray-500">
                        Net Pay
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                        Status
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-gray-500">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {payslipsLoading && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-4 py-4 text-center text-sm text-gray-500"
                        >
                          Loading...
                        </td>
                      </tr>
                    )}
                    {!payslipsLoading && payslips.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-4 py-4 text-center text-sm text-gray-500"
                        >
                          No payslips found
                        </td>
                      </tr>
                    )}
                    {payslips.map((row) => (
                      <tr key={row.id}>
                        <td className="px-4 py-3 text-sm text-gray-800">
                          <div className="flex flex-col">
                            <span className="font-semibold">
                              {row.employee?.firstName || row.employee?.name
                                ? `${row.employee.firstName || ""} ${
                                    row.employee.lastName || ""
                                  }`.trim() || row.employee.name
                                : row.employeeId}
                            </span>
                            <span className="text-xs text-gray-500">
                              {row.employee?.employeeCode || "-"}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#444]">
                          {row.periodStart} - {row.periodEnd}
                        </td>
                        <td className="px-4 py-3 text-sm text-[#444]">
                          {row.payDate}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-gray-800">
                          {numberOrZero(row.netPay).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                              row.status === "generated"
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-100 text-[#444]"
                            }`}
                          >
                            {row.status || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          <button
                            onClick={() => downloadPayslip(row.id)}
                            className="rounded-lg border border-indigo-600 px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-50"
                          >
                            Download PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Salary modal */}
      <SimpleModal
        open={salaryModalOpen}
        title={`Update Salary • ${salaryForm.name}`}
        onClose={() => setSalaryModalOpen(false)}
        footer={
          <>
            <button
              onClick={() => setSalaryModalOpen(false)}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-[#444]"
            >
              Cancel
            </button>
            <button
              onClick={saveSalary}
              className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow hover:shadow-md"
            >
              Save
            </button>
          </>
        }
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-xs font-semibold text-[#444]">
              Basic Salary *
            </label>
            <input
              type="number"
              value={salaryForm.basicSalary}
              onChange={(e) =>
                setSalaryForm((prev) => ({
                  ...prev,
                  basicSalary: e.target.value,
                }))
              }
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none focus:ring-1 focus:ring-primaryBlue"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#444]">
              Pay frequency
            </label>
            <select
              value={salaryForm.payFrequency}
              onChange={(e) =>
                setSalaryForm((prev) => ({
                  ...prev,
                  payFrequency: e.target.value,
                }))
              }
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none focus:ring-1 focus:ring-primaryBlue"
            >
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#444]">
              Effective from *
            </label>
            <div className="relative mt-1">
              <input
                type="text"
                readOnly
                value={formatDateForDisplay(salaryForm.effectiveFrom)}
                onClick={() => {
                  setShowSalaryFormEffectiveFromPicker(!showSalaryFormEffectiveFromPicker);
                }}
                className="w-full rounded-lg border border-gray-200 pl-3 pr-11 py-2.5 text-xs text-gray-900 focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-primaryBlue/20 transition-colors cursor-pointer"
                placeholder=""
              />
              {!salaryForm.effectiveFrom && (
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                  <span className="text-xs text-gray-400">Select date</span>
                </div>
              )}
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              {showSalaryFormEffectiveFromPicker && (
                <div className="absolute top-full left-0 z-50 mt-2 p-2.5">
                  <Calendar
                    onChange={(date) => {
                      const formattedDate = date ? formatDateToISO(date) : '';
                      setSalaryForm((prev) => ({ ...prev, effectiveFrom: formattedDate }));
                      setShowSalaryFormEffectiveFromPicker(false);
                    }}
                    value={parseDateString(salaryForm.effectiveFrom)}
                    formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                    prevLabel={<span className="text-purple-600">‹</span>}
                    nextLabel={<span className="text-purple-600">›</span>}
                    prev2Label={null}
                    next2Label={null}
                    tileClassName={({ date, view }) => {
                      if (view === 'month') {
                        const day = date.getDay();
                        if (day === 0 || day === 6) {
                          return 'weekend-day';
                        }
                      }
                      return null;
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </SimpleModal>

      {/* Payroll item modal */}
      <SimpleModal
        open={itemModalOpen}
        title={itemForm.id ? "Edit Payroll Item" : "New Payroll Item"}
        onClose={() => setItemModalOpen(false)}
        slideFromRight={true}
        footer={
          <>
            <button
              onClick={() => setItemModalOpen(false)}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-[#444] hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={saveItem}
              className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow hover:shadow-md transition-all"
            >
              Save
            </button>
          </>
        }
      >
        <div className="grid gap-4 grid-cols-1">
          <div>
            <label className="text-xs font-normal text-gray-600 mb-2">
              Name *
            </label>
            <input
              type="text"
              value={itemForm.name}
              onChange={(e) =>
                setItemForm((prev) => ({ ...prev, name: e.target.value }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-[#444] focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all"
            />
          </div>
          <div>
            <label className="text-xs font-normal text-gray-600 mb-2">
              Type *
            </label>
            <select
              value={itemForm.type}
              onChange={(e) =>
                setItemForm((prev) => ({ ...prev, type: e.target.value }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-[#444] focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all bg-white"
            >
              <option value="addition">Addition</option>
              <option value="deduction">Deduction</option>
              <option value="incentive">Incentive</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-normal text-gray-600 mb-2">
              Category
            </label>
            <input
              type="text"
              value={itemForm.category}
              onChange={(e) =>
                setItemForm((prev) => ({ ...prev, category: e.target.value }))
              }
              placeholder="e.g. Monthly Allowance"
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-[#444] placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all"
            />
          </div>
          <div>
            <label className="text-xs font-normal text-gray-600 mb-2">
              Default amount
            </label>
            <input
              type="number"
              value={itemForm.defaultAmount}
              onChange={(e) =>
                setItemForm((prev) => ({
                  ...prev,
                  defaultAmount: e.target.value,
                }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-[#444] focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all"
            />
          </div>
          <div>
            <label className="text-xs font-normal text-gray-600 mb-2">
              Status
            </label>
            <select
              value={itemForm.isActive ? "active" : "inactive"}
              onChange={(e) =>
                setItemForm((prev) => ({
                  ...prev,
                  isActive: e.target.value === "active",
                }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-[#444] focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all bg-white"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </SimpleModal>

      {/* Recurring incentive modal */}
      <SimpleModal
        open={recurringModalOpen}
        title="Add Recurring Incentive"
        onClose={() => setRecurringModalOpen(false)}
        slideFromRight={true}
        footer={
          <>
            <button
              onClick={() => setRecurringModalOpen(false)}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-[#444] hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={saveRecurringIncentive}
              className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow hover:shadow-md transition-all"
            >
              Save
            </button>
          </>
        }
      >
        <div className="grid gap-4 grid-cols-1">
          <div>
            <label className="text-xs font-normal text-gray-600 mb-2">
              Employee *
            </label>
            <select
              value={incentiveEmployeeId}
              onChange={(e) => setIncentiveEmployeeId(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-[#444] focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all bg-white"
            >
              <option value="">Select</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {getEmployeeNameById(emp.id)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-normal text-gray-600 mb-2">
              Incentive Item *
            </label>
            <select
              value={incentiveForm.payrollItemId}
              onChange={(e) =>
                setIncentiveForm((prev) => ({
                  ...prev,
                  payrollItemId: e.target.value,
                }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-[#444] focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all bg-white"
            >
              <option value="">Select</option>
              {incentiveItems.map((it) => (
                <option key={it.id} value={it.id}>
                  {it.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-normal text-gray-600 mb-2">
              Amount *
            </label>
            <input
              type="number"
              value={incentiveForm.amount}
              onChange={(e) =>
                setIncentiveForm((prev) => ({
                  ...prev,
                  amount: e.target.value,
                }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-[#444] focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all"
            />
          </div>
          <div>
            <label className="text-xs font-normal text-gray-600 mb-2">
              Start month *
            </label>
            <input
              type="month"
              value={incentiveForm.startMonth}
              onChange={(e) =>
                setIncentiveForm((prev) => ({
                  ...prev,
                  startMonth: e.target.value,
                }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-[#444] focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all"
            />
          </div>
          <div>
            <label className="text-xs font-normal text-gray-600 mb-2">
              End month
            </label>
            <input
              type="month"
              value={incentiveForm.endMonth}
              onChange={(e) =>
                setIncentiveForm((prev) => ({
                  ...prev,
                  endMonth: e.target.value,
                }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-[#444] focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all"
            />
          </div>
          <div>
            <label className="text-xs font-normal text-gray-600 mb-2">
              Notes
            </label>
            <textarea
              rows={3}
              value={incentiveForm.notes}
              onChange={(e) =>
                setIncentiveForm((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-[#444] focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all resize-none"
            />
          </div>
        </div>
      </SimpleModal>

      {/* Monthly incentive modal */}
      <SimpleModal
        open={monthlyModalOpen}
        title="Add Monthly Incentive"
        onClose={() => setMonthlyModalOpen(false)}
        slideFromRight={true}
        footer={
          <>
            <button
              onClick={() => setMonthlyModalOpen(false)}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-[#444] hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={saveMonthlyIncentive}
              className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow hover:shadow-md transition-all"
            >
              Save
            </button>
          </>
        }
      >
        <div className="grid gap-4 grid-cols-1">
          <div>
            <label className="text-xs font-normal text-gray-600 mb-2">
              Employee *
            </label>
            <select
              value={incentiveEmployeeId}
              onChange={(e) => setIncentiveEmployeeId(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-[#444] focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all bg-white"
            >
              <option value="">Select</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {getEmployeeNameById(emp.id)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-normal text-gray-600 mb-2">
              Incentive Item *
            </label>
            <select
              value={monthlyIncentiveForm.payrollItemId}
              onChange={(e) =>
                setMonthlyIncentiveForm((prev) => ({
                  ...prev,
                  payrollItemId: e.target.value,
                }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-[#444] focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all bg-white"
            >
              <option value="">Select</option>
              {incentiveItems.map((it) => (
                <option key={it.id} value={it.id}>
                  {it.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-normal text-gray-600 mb-2">
              Amount *
            </label>
            <input
              type="number"
              value={monthlyIncentiveForm.amount}
              onChange={(e) =>
                setMonthlyIncentiveForm((prev) => ({
                  ...prev,
                  amount: e.target.value,
                }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-[#444] focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all"
            />
          </div>
          <div>
            <label className="text-xs font-normal text-gray-600 mb-2">
              Month *
            </label>
            <input
              type="month"
              value={monthlyIncentiveForm.month}
              onChange={(e) =>
                setMonthlyIncentiveForm((prev) => ({
                  ...prev,
                  month: e.target.value,
                }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-[#444] focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all"
            />
          </div>
          <div>
            <label className="text-xs font-normal text-gray-600 mb-2">
              Notes
            </label>
            <textarea
              rows={3}
              value={monthlyIncentiveForm.notes}
              onChange={(e) =>
                setMonthlyIncentiveForm((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-[#444] focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all resize-none"
            />
          </div>
        </div>
      </SimpleModal>

      {/* Generate payslip modal */}
      <SimpleModal
        open={generateModalOpen}
        title={`Generate Payslip • ${generateForm.name}`}
        onClose={() => setGenerateModalOpen(false)}
        footer={
          <>
            <button
              onClick={() => setGenerateModalOpen(false)}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-[#444]"
            >
              Close
            </button>
            <button
              onClick={loadPreview}
              className="rounded-lg border border-indigo-600 px-4 py-2 text-sm font-semibold text-indigo-600 hover:bg-indigo-50"
            >
              Preview
            </button>
            <button
              onClick={generatePayslip}
              className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow hover:shadow-md"
            >
              Generate
            </button>
          </>
        }
        widthClass="max-w-3xl"
      >
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="text-xs font-semibold text-[#444]">
              Period start *
            </label>
            <div className="relative mt-1">
              <input
                type="text"
                readOnly
                value={formatDateForDisplay(generateForm.periodStart)}
                onClick={() => {
                  setShowPeriodStartPicker(!showPeriodStartPicker);
                  setShowPeriodEndPicker(false);
                  setShowPayDatePicker(false);
                }}
                className="w-full rounded-lg border border-gray-200 pl-3 pr-11 py-2.5 text-xs text-gray-900 focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-primaryBlue/20 transition-colors cursor-pointer"
                placeholder=""
              />
              {!generateForm.periodStart && (
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                  <span className="text-xs text-gray-400">Select date</span>
                </div>
              )}
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              {showPeriodStartPicker && (
                <div className="absolute top-full left-0 z-50 mt-2 p-2.5">
                  <Calendar
                    onChange={(date) => {
                      const formattedDate = date ? formatDateToISO(date) : '';
                      setGenerateForm((prev) => ({ ...prev, periodStart: formattedDate }));
                      setShowPeriodStartPicker(false);
                    }}
                    value={parseDateString(generateForm.periodStart)}
                    formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                    prevLabel={<span className="text-purple-600">‹</span>}
                    nextLabel={<span className="text-purple-600">›</span>}
                    prev2Label={null}
                    next2Label={null}
                    tileClassName={({ date, view }) => {
                      if (view === 'month') {
                        const day = date.getDay();
                        if (day === 0 || day === 6) {
                          return 'weekend-day';
                        }
                      }
                      return null;
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#444]">
              Period end *
            </label>
            <div className="relative mt-1">
              <input
                type="text"
                readOnly
                value={formatDateForDisplay(generateForm.periodEnd)}
                onClick={() => {
                  setShowPeriodEndPicker(!showPeriodEndPicker);
                  setShowPeriodStartPicker(false);
                  setShowPayDatePicker(false);
                }}
                className="w-full rounded-lg border border-gray-200 pl-3 pr-11 py-2.5 text-xs text-gray-900 focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-primaryBlue/20 transition-colors cursor-pointer"
                placeholder=""
              />
              {!generateForm.periodEnd && (
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                  <span className="text-xs text-gray-400">Select date</span>
                </div>
              )}
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              {showPeriodEndPicker && (
                <div className="absolute top-full left-0 z-50 mt-2 p-2.5">
                  <Calendar
                    onChange={(date) => {
                      const formattedDate = date ? formatDateToISO(date) : '';
                      setGenerateForm((prev) => ({ ...prev, periodEnd: formattedDate }));
                      setShowPeriodEndPicker(false);
                    }}
                    value={parseDateString(generateForm.periodEnd)}
                    formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                    prevLabel={<span className="text-purple-600">‹</span>}
                    nextLabel={<span className="text-purple-600">›</span>}
                    prev2Label={null}
                    next2Label={null}
                    tileClassName={({ date, view }) => {
                      if (view === 'month') {
                        const day = date.getDay();
                        if (day === 0 || day === 6) {
                          return 'weekend-day';
                        }
                      }
                      return null;
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#444]">
              Pay date
            </label>
            <div className="relative mt-1">
              <input
                type="text"
                readOnly
                value={formatDateForDisplay(generateForm.payDate)}
                onClick={() => {
                  setShowPayDatePicker(!showPayDatePicker);
                  setShowPeriodStartPicker(false);
                  setShowPeriodEndPicker(false);
                }}
                className="w-full rounded-lg border border-gray-200 pl-3 pr-11 py-2.5 text-xs text-gray-900 focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-primaryBlue/20 transition-colors cursor-pointer"
                placeholder=""
              />
              {!generateForm.payDate && (
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                  <span className="text-xs text-gray-400">Select date</span>
                </div>
              )}
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              {showPayDatePicker && (
                <div className="absolute top-full left-0 z-50 mt-2 p-2.5">
                  <Calendar
                    onChange={(date) => {
                      const formattedDate = date ? formatDateToISO(date) : '';
                      setGenerateForm((prev) => ({ ...prev, payDate: formattedDate }));
                      setShowPayDatePicker(false);
                    }}
                    value={parseDateString(generateForm.payDate)}
                    formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                    prevLabel={<span className="text-purple-600">‹</span>}
                    nextLabel={<span className="text-purple-600">›</span>}
                    prev2Label={null}
                    next2Label={null}
                    tileClassName={({ date, view }) => {
                      if (view === 'month') {
                        const day = date.getDay();
                        if (day === 0 || day === 6) {
                          return 'weekend-day';
                        }
                      }
                      return null;
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="mt-4">
          {previewLoading && (
            <div className="rounded-lg border border-dashed border-gray-200 p-4 text-sm text-gray-500">
              Generating preview...
            </div>
          )}
          {!previewLoading && previewData && (
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4 text-sm text-gray-800">
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <div className="font-semibold">
                    {previewData.employee?.name} (
                    {previewData.employee?.employeeCode})
                  </div>
                  <div className="text-xs text-gray-500">
                    {previewData.employee?.designation} •{" "}
                    {previewData.employee?.department}
                  </div>
                </div>
                <div className="text-right text-xs text-gray-500">
                  <div>
                    Period: {previewData.periodStart} - {previewData.periodEnd}
                  </div>
                  <div>Pay date: {previewData.payDate}</div>
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-lg bg-white p-3 shadow-sm">
                  <div className="text-xs text-gray-500">Earnings</div>
                  <div className="mt-1 text-lg font-semibold text-gray-800">
                    {numberOrZero(previewData.grossEarnings).toLocaleString()}
                  </div>
                </div>
                <div className="rounded-lg bg-white p-3 shadow-sm">
                  <div className="text-xs text-gray-500">Deductions</div>
                  <div className="mt-1 text-lg font-semibold text-red-600">
                    {numberOrZero(previewData.totalDeductions).toLocaleString()}
                  </div>
                </div>
                <div className="rounded-lg bg-white p-3 shadow-sm">
                  <div className="text-xs text-gray-500">Net pay</div>
                  <div className="mt-1 text-lg font-semibold text-green-600">
                    {numberOrZero(previewData.netPay).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </SimpleModal>

      {/* Employee payroll item modal */}
      <SimpleModal
        open={employeePayrollModalOpen}
        title={
          employeePayrollForm.id
            ? `Edit Payroll Item - ${getEmployeeNameById(incentiveEmployeeId)}`
            : `Add Payroll Item - ${getEmployeeNameById(incentiveEmployeeId)}`
        }
        onClose={() => setEmployeePayrollModalOpen(false)}
        widthClass="max-w-4xl"
        footer={
          <>
            <button
              onClick={() => setEmployeePayrollModalOpen(false)}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-[#444]"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                if (!incentiveEmployeeId) {
                  toast.error("Select an employee");
                  return;
                }
                if (!employeePayrollForm.payrollItemId) {
                  toast.error("Select a payroll item");
                  return;
                }
                if (
                  employeePayrollForm.amount === "" ||
                  employeePayrollForm.amount === null ||
                  employeePayrollForm.amount === undefined
                ) {
                  toast.error("Enter an amount");
                  return;
                }
                if (!employeePayrollForm.effectiveFrom) {
                  toast.error("Select effective from date");
                  return;
                }
                try {
                  if (employeePayrollForm.id) {
                    // Update: only send updatable fields (no payrollItemId, no replaceExisting)
                    const updatePayload = {
                      amount: Number(employeePayrollForm.amount),
                      effectiveFrom: employeePayrollForm.effectiveFrom,
                      effectiveTo: employeePayrollForm.effectiveTo || null,
                      isActive: !!employeePayrollForm.isActive,
                      notes: employeePayrollForm.notes || null,
                    };
                    await axios.put(
                      `${payrollEmployeePayrollItemApi}/${employeePayrollForm.id}`,
                      updatePayload,
                      { withCredentials: true }
                    );
                    toast.success("Employee payroll item updated");
                    // Close modal after update
                    setEmployeePayrollModalOpen(false);
                  } else {
                    // Add: send all required fields including payrollItemId and replaceExisting
                    const addPayload = {
                      payrollItemId: employeePayrollForm.payrollItemId,
                      amount: Number(employeePayrollForm.amount),
                      effectiveFrom: employeePayrollForm.effectiveFrom,
                      effectiveTo: employeePayrollForm.effectiveTo || null,
                      notes: employeePayrollForm.notes || null,
                      replaceExisting: !!employeePayrollForm.replaceExisting,
                    };
                    await axios.post(
                      `${payrollEmployeeIncentivesBaseApi}/${incentiveEmployeeId}/payroll-items`,
                      addPayload,
                      { withCredentials: true }
                    );
                    toast.success("Employee payroll item added");
                    // Keep modal open for adding more items, but reset form
                    setEmployeePayrollForm({
                      id: null,
                      payrollItemId: "",
                      type: "addition",
                      amount: "",
                      effectiveFrom: todayISO(),
                      effectiveTo: "",
                      notes: "",
                      isActive: true,
                      replaceExisting: true,
                    });
                  }
                  // Refresh list to show new/updated item
                  fetchEmployeePayrollItems(incentiveEmployeeId);
                } catch (error) {
                  console.error("save employee payroll item error", error);
                  const msg =
                    error?.response?.data?.message ||
                    "Unable to save employee payroll item";
                  toast.error(msg);
                }
              }}
              className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow hover:shadow-md"
            >
              Save
            </button>
          </>
        }
      >
        {/* Existing assigned payroll items table */}
        <div className="mb-6">
          <h4 className="mb-3 text-sm font-semibold text-gray-800">
            Currently Assigned Payroll Items
          </h4>
          {employeePayrollLoading && (
            <div className="rounded-lg border border-gray-200 p-4 text-center text-sm text-gray-500">
              Loading assigned items...
            </div>
          )}
          {!employeePayrollLoading && employeePayrollItems.length === 0 && (
            <div className="rounded-lg border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500">
              No payroll items assigned yet
            </div>
          )}
          {!employeePayrollLoading && employeePayrollItems.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-gray-100">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                        Item Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                        Effective Period
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-gray-500">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                        Notes
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase text-gray-500">
                        Status
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-gray-500">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {employeePayrollItems.map((row) => (
                      <tr key={row.id}>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-800">
                          {row.payrollItem?.name || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-[#444]">
                          <span
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                              (row.payrollItem?.type || "").toLowerCase() === "addition"
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {(row.payrollItem?.type || "N/A").toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#444]">
                          <div className="flex flex-col">
                            <span>From: {row.effectiveFrom || "-"}</span>
                            {row.effectiveTo && (
                              <span className="text-xs text-gray-500">
                                To: {row.effectiveTo}
                              </span>
                            )}
                            {!row.effectiveTo && (
                              <span className="text-xs text-gray-500">Ongoing</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-gray-800">
                          {numberOrZero(row.amount || row.payrollItem?.defaultAmount).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-[#444]">
                          {row.notes || "-"}
                        </td>
                        <td className="px-4 py-3 text-center text-sm">
                          <span
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                              row.isActive !== false
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {row.isActive !== false ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => {
                                const selectedItem = additionAndDeductionItems.find(
                                  (x) => String(x.id) === String(row.payrollItemId)
                                );
                                setEmployeePayrollForm({
                                  id: row.id,
                                  payrollItemId: row.payrollItemId,
                                  type: (row.payrollItem?.type || "addition").toLowerCase(),
                                  amount: row.amount ?? row.payrollItem?.defaultAmount ?? "",
                                  effectiveFrom: row.effectiveFrom || todayISO(),
                                  effectiveTo: row.effectiveTo || "",
                                  notes: row.notes || "",
                                  isActive: row.isActive !== false,
                                  replaceExisting: false,
                                });
                                // Load items for dropdown (in case not loaded)
                                setItemsTab("addition");
                                fetchItems();
                                // Scroll form into view
                                setTimeout(() => {
                                  const formSection = document.querySelector('[data-payroll-form]');
                                  if (formSection) {
                                    formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  }
                                }, 100);
                              }}
                              className="rounded-lg border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                            >
                              Edit
                            </button>
                            <button
                              onClick={async () => {
                                if (
                                  !window.confirm(
                                    `Remove "${row.payrollItem?.name || "this item"}" from this employee?`
                                  )
                                )
                                  return;
                                try {
                                  await axios.delete(
                                    `${payrollEmployeePayrollItemApi}/${row.id}`,
                                    { withCredentials: true }
                                  );
                                  toast.success("Payroll item removed");
                                  // Refresh the list
                                  fetchEmployeePayrollItems(incentiveEmployeeId);
                                  // Reset form if editing this item
                                  if (employeePayrollForm.id === row.id) {
                                    setEmployeePayrollForm({
                                      id: null,
                                      payrollItemId: "",
                                      type: "addition",
                                      amount: "",
                                      effectiveFrom: todayISO(),
                                      effectiveTo: "",
                                      notes: "",
                                      isActive: true,
                                      replaceExisting: true,
                                    });
                                  }
                                } catch (error) {
                                  console.error("delete employee payroll item error", error);
                                  toast.error("Unable to remove payroll item");
                                }
                              }}
                              className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                            >
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="my-6 border-t border-gray-200"></div>

        {/* Add new payroll item form */}
        <div data-payroll-form>
          <h4 className="mb-4 text-sm font-semibold text-gray-800">
            {employeePayrollForm.id ? "Edit Payroll Item" : "Add New Payroll Item"}
          </h4>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-[#444]">
                Payroll Item *
              </label>
              {employeePayrollForm.id ? (
                <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-[#444]">
                  {employeePayrollItems.find((x) => x.id === employeePayrollForm.id)?.payrollItem?.name || "N/A"} (
                  {(employeePayrollItems.find((x) => x.id === employeePayrollForm.id)?.payrollItem?.type || "N/A").toUpperCase()})
                  <span className="ml-2 text-xs text-gray-500">(Cannot be changed when editing)</span>
                </div>
              ) : (
                <select
                  value={employeePayrollForm.payrollItemId}
                  onChange={(e) => {
                    const id = e.target.value;
                    const selected = additionAndDeductionItems.find(
                      (x) => String(x.id) === String(id)
                    );
                    setEmployeePayrollForm((prev) => ({
                      ...prev,
                      payrollItemId: id,
                      type: (selected?.type || prev.type || "addition").toLowerCase(),
                      amount:
                        prev.amount === "" && selected && selected.defaultAmount != null
                          ? selected.defaultAmount
                          : prev.amount,
                    }));
                  }}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                >
                  <option value="">Select payroll item</option>
                  {additionAndDeductionItems.map((pi) => (
                    <option key={pi.id} value={pi.id}>
                      {pi.name}{" "}
                      {pi.type ? `(${pi.type === "addition" ? "Addition" : "Deduction"})` : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>
          <div>
            <label className="text-xs font-semibold text-[#444]">Amount *</label>
            <input
              type="number"
              value={employeePayrollForm.amount}
              onChange={(e) =>
                setEmployeePayrollForm((prev) => ({
                  ...prev,
                  amount: e.target.value,
                }))
              }
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none focus:ring-1 focus:ring-primaryBlue"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#444]">
              Effective From *
            </label>
            <div className="relative mt-1">
              <input
                type="text"
                readOnly
                value={formatDateForDisplay(employeePayrollForm.effectiveFrom)}
                onClick={() => {
                  setShowEmployeePayrollEffectiveFromPicker(!showEmployeePayrollEffectiveFromPicker);
                  setShowEmployeePayrollEffectiveToPicker(false);
                }}
                className="w-full rounded-lg border border-gray-200 pl-3 pr-11 py-2.5 text-xs text-gray-900 focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-primaryBlue/20 transition-colors cursor-pointer"
                placeholder=""
              />
              {!employeePayrollForm.effectiveFrom && (
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                  <span className="text-xs text-gray-400">Select date</span>
                </div>
              )}
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              {showEmployeePayrollEffectiveFromPicker && (
                <div className="absolute top-full left-0 z-50 mt-2 p-2.5">
                  <Calendar
                    onChange={(date) => {
                      const formattedDate = date ? formatDateToISO(date) : '';
                      setEmployeePayrollForm((prev) => ({ ...prev, effectiveFrom: formattedDate }));
                      setShowEmployeePayrollEffectiveFromPicker(false);
                    }}
                    value={parseDateString(employeePayrollForm.effectiveFrom)}
                    formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                    prevLabel={<span className="text-purple-600">‹</span>}
                    nextLabel={<span className="text-purple-600">›</span>}
                    prev2Label={null}
                    next2Label={null}
                    tileClassName={({ date, view }) => {
                      if (view === 'month') {
                        const day = date.getDay();
                        if (day === 0 || day === 6) {
                          return 'weekend-day';
                        }
                      }
                      return null;
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#444]">
              Effective To
            </label>
            <div className="relative mt-1">
              <input
                type="text"
                readOnly
                value={formatDateForDisplay(employeePayrollForm.effectiveTo)}
                onClick={() => {
                  setShowEmployeePayrollEffectiveToPicker(!showEmployeePayrollEffectiveToPicker);
                  setShowEmployeePayrollEffectiveFromPicker(false);
                }}
                className="w-full rounded-lg border border-gray-200 pl-3 pr-11 py-2.5 text-xs text-gray-900 focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-primaryBlue/20 transition-colors cursor-pointer"
                placeholder=""
              />
              {!employeePayrollForm.effectiveTo && (
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                  <span className="text-xs text-gray-400">Select date</span>
                </div>
              )}
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              {showEmployeePayrollEffectiveToPicker && (
                <div className="absolute top-full right-0 z-50 mt-2 p-2.5">
                  <Calendar
                    onChange={(date) => {
                      const formattedDate = date ? formatDateToISO(date) : '';
                      setEmployeePayrollForm((prev) => ({ ...prev, effectiveTo: formattedDate }));
                      setShowEmployeePayrollEffectiveToPicker(false);
                    }}
                    value={parseDateString(employeePayrollForm.effectiveTo)}
                    formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                    prevLabel={<span className="text-purple-600">‹</span>}
                    nextLabel={<span className="text-purple-600">›</span>}
                    prev2Label={null}
                    next2Label={null}
                    tileClassName={({ date, view }) => {
                      if (view === 'month') {
                        const day = date.getDay();
                        if (day === 0 || day === 6) {
                          return 'weekend-day';
                        }
                      }
                      return null;
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          {!employeePayrollForm.id && (
            <div className="md:col-span-2 flex items-center gap-2">
              <input
                id="replaceExisting"
                type="checkbox"
                checked={!!employeePayrollForm.replaceExisting}
                onChange={(e) =>
                  setEmployeePayrollForm((prev) => ({
                    ...prev,
                    replaceExisting: e.target.checked,
                  }))
                }
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-primaryBlue"
              />
              <label htmlFor="replaceExisting" className="text-xs text-[#444]">
                Deactivate any existing active record for this employee and payroll
                item on or after the selected start date
              </label>
            </div>
          )}
          {employeePayrollForm.id && (
            <div className="md:col-span-2 flex items-center gap-2">
              <input
                id="isActive"
                type="checkbox"
                checked={!!employeePayrollForm.isActive}
                onChange={(e) =>
                  setEmployeePayrollForm((prev) => ({
                    ...prev,
                    isActive: e.target.checked,
                  }))
                }
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-primaryBlue"
              />
              <label htmlFor="isActive" className="text-xs text-[#444]">
                Active (uncheck to deactivate this payroll item)
              </label>
            </div>
          )}
          <div className="md:col-span-2">
            <label className="text-xs font-semibold text-[#444]">Notes</label>
            <textarea
              rows={3}
              value={employeePayrollForm.notes}
              onChange={(e) =>
                setEmployeePayrollForm((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none focus:ring-1 focus:ring-primaryBlue"
              placeholder="Optional notes about this payroll item"
            />
          </div>
        </div>
        </div>
      </SimpleModal>

      <ToastContainer />
    </div>
  );
};

export default Payroll;


